/**
 * Style Isolation Utilities for Micro-Frontend Architecture
 *
 * These utilities help prevent CSS conflicts between the host application
 * and remote micro-frontend applications that use different CSS frameworks.
 */
/**
 * Marks host application styles to prevent them from being scoped
 */
export const markHostStyles = () => {
    const styles = document.querySelectorAll('style');
    for (const style of styles) {
        if (!style.dataset.remoteApp && !style.dataset.app) {
            style.dataset.app = 'host';
        }
    }
};
/**
 * Cleans up remote application styles when unmounting
 */
export const cleanupRemoteStyles = (appName) => {
    const remoteStyles = document.querySelectorAll(`style[data-remote-app="${appName}"], link[data-remote-app="${appName}"]`);
    for (const style of remoteStyles) {
        style.remove();
    }
};
/**
export const scopeCSS = (css: string, scopeSelector: string): string => {
    try {
        // Simple CSS scoping - adds scope prefix to all selectors
        // This is a basic implementation; for production, consider using a CSS parser
        const rules = css.split('}').filter(Boolean)

        return rules
            .map((rule) => {
                const [selector, ...declarationParts] = rule.split('{')
                if (!selector || declarationParts.length === 0) return rule

                const declarations = declarationParts.join('{')
                const selectors = selector.split(',').map((s) => s.trim())

                const scopedSelectors = selectors.map((sel) => {
                    // Don't scope @-rules
                    if (sel.startsWith('@')) return sel

                    // Handle :root, html, body specially
                    if (sel === ':root' || sel === 'html' || sel === 'body') {
                        return `${scopeSelector} ${sel}, ${scopeSelector}`
                    }

                    // Scope regular selectors
                    return `${scopeSelector} ${sel}`
                })

                return `${scopedSelectors.join(', ')} { ${declarations} }`
            })
            .join('\n')
    } catch (error) {
        console.error('Error scoping CSS:', error)
        return css
    }
}

/**
 * Creates a sandboxed environment for remote app styles
 */
export const createStyleSandbox = (appName) => {
    const sandbox = {
        injectedStyles: new Set(),
        /**
         * Inject scoped styles for the remote app
         */
        injectStyle(css, scope) {
            const scopedCSS = scopeCSS(css, scope);
            const styleElement = document.createElement('style');
            styleElement.textContent = scopedCSS;
            styleElement.dataset.remoteApp = appName;
            styleElement.dataset.scoped = 'true';
            document.head.appendChild(styleElement);
            this.injectedStyles.add(styleElement);
            return styleElement;
        },
        /**
         * Clean up all injected styles
         */
        cleanup() {
            for (const style of this.injectedStyles) {
                style.remove();
            }
            this.injectedStyles.clear();
        }
    };
    return sandbox;
};
/**
 * CSS Reset for remote apps to prevent inheritance of host styles
 */
export const getResetCSS = (scopeSelector) => {
    return `
		${scopeSelector} {
			/* Reset box model */
			all: initial;
			display: block;
			
			/* Re-enable box-sizing */
			*, *::before, *::after {
				box-sizing: border-box;
			}
		}
		
		${scopeSelector} * {
			/* Reset inherited styles */
			font-family: inherit;
			line-height: inherit;
		}
	`;
};
/**
 * Monitors and logs CSS conflicts between host and remote apps
 */
export const createCSSConflictMonitor = () => {
    const conflicts = new Map();
    return {
        /**
         * Check for style conflicts between host and remote
         */
        checkConflicts(element, appName) {
            const computedStyle = globalThis.window.getComputedStyle(element);
            const hostStyles = element.dataset.app === 'host';
            const remoteStyles = element.dataset.remoteApp === appName;
            if (hostStyles && remoteStyles) {
                conflicts.set(element.className, {
                    host: computedStyle.cssText,
                    remote: computedStyle.cssText
                });
            }
            return conflicts;
        },
        /**
         * Get all detected conflicts
         */
        getConflicts() {
            return Array.from(conflicts.entries());
        },
        /**
         * Clear conflict history
         */
        clearConflicts() {
            conflicts.clear();
        }
    };
};
export const defaultStyleIsolationConfig = {
    enableScoping: true,
    enableMonitoring: false,
    enableReset: false,
    scopePrefix: 'data-remote-app'
};
